import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, CircleDashedIcon, MapPinIcon } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import { brands, campaigns, markets } from '../data/quotation';
type TabId = 'brands' | 'campaigns' | 'markets';
const TABS: {
  id: TabId;
  label: string;
  count: string;
}[] = [{
  id: 'brands',
  label: 'Global brands',
  count: '3'
}, {
  id: 'campaigns',
  label: 'Master campaigns',
  count: '5'
}, {
  id: 'markets',
  label: 'EU5 markets',
  count: '5'
}];
export function ScopeSection() {
  const [tab, setTab] = useState<TabId>('brands');
  return <section id="scope" className="border-t border-line bg-white py-20 sm:py-28" data-id="element-167" data-mp="3exy8bvvxx818">
      <div className="mx-auto w-full max-w-7xl px-5 sm:px-8" data-id="element-168" data-mp="3jw9he4eeveie">
        <SectionHeading eyebrow="The scope" title="Three corporate brands × five master campaigns × five markets" description="A fixed, quoted scope focused on the strategic corporate brands where digital impact matters most, with a clear path to scale to more countries, brands and campaigns. Explore each dimension below." data-id="element-169" data-mp="1d5a2derww6hr" />

        <div role="tablist" aria-label="Scope dimensions" className="mt-12 inline-flex flex-wrap gap-1 rounded-full border border-line bg-surface p-1" data-id="element-170" data-mp="2x5djs67j9l9z">
          {TABS.map(item => <button key={item.id} role="tab" type="button" id={`scope-tab-${item.id}`} aria-selected={tab === item.id} aria-controls={`scope-panel-${item.id}`} onClick={() => setTab(item.id)} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${tab === item.id ? 'bg-primary text-white' : 'text-ink-500 hover:text-ink-900'}`} data-id="element-171" data-mp="2ia7935xhsyab">
              {item.label}
              <span className={`ml-2 text-xs ${tab === item.id ? 'text-white/70' : 'text-ink-300'}`} data-id="element-172" data-mp="aipiofk6jvf6">
                {item.count}
              </span>
            </button>)}
        </div>

        <AnimatePresence mode="wait" data-id="element-173" data-mp="oja0p9i9tn7m">
          <motion.div key={tab} id={`scope-panel-${tab}`} role="tabpanel" aria-labelledby={`scope-tab-${tab}`} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.25
        }} className="mt-8" data-id="element-174" data-mp="oi7az4wopqta">
            {tab === 'brands' ? <BrandsPanel data-id="element-175" data-mp="5lpk0ppntuy3" /> : null}
            {tab === 'campaigns' ? <CampaignsPanel data-id="element-176" data-mp="34ao2l2xduidv" /> : null}
            {tab === 'markets' ? <MarketsPanel data-id="element-177" data-mp="34a4vyid6ngf5" /> : null}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}
function BrandsPanel() {
  return <div className="grid gap-6 md:grid-cols-3" data-id="element-178" data-mp="31pygpej4c3ja">
      {brands.map(brand => <article key={brand.id} className="rounded-2xl border border-line bg-white p-6" data-id="element-179" data-mp="31nvr2vlvocf3">
          <p className="text-xs uppercase tracking-[0.18em] text-ink-300" data-id="element-180" data-mp="2rrsk9cg2zplt">
            {brand.therapyArea}
          </p>
          <h3 className="mt-3 font-display text-xl font-semibold text-ink-900" data-id="element-181" data-mp="2mrxq9010ejwj">
            {brand.name}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-ink-500" data-id="element-182" data-mp="2isxbbslnk1et">
            {brand.note}
          </p>
          <p className="mt-5 border-t border-line pt-4 text-xs text-ink-300" data-id="element-183" data-mp="2dzclinu7qdqp">
            Strategic corporate brand · Phase 1
          </p>
        </article>)}
    </div>;
}
function CampaignsPanel() {
  return <ol className="overflow-hidden rounded-2xl border border-line" data-id="element-184" data-mp="240n1aoeugh7a">
      {campaigns.map((campaign, index) => <li key={campaign.id} className="flex flex-col gap-3 border-b border-line bg-white p-6 last:border-b-0 sm:flex-row sm:items-start sm:gap-6" data-id="element-185" data-mp="1v1rtj7w5iz1v" data-mpi={index}>
          <span className="font-display text-sm font-bold text-primary sm:w-10" data-id="element-186" data-mp="2juina0wb0t8r">
            {String(index + 1).padStart(2, '0')}
          </span>
          <div className="flex-1" data-id="element-187" data-mp="2b0x8ihk53bzc">
            <h3 className="font-display text-lg font-semibold text-ink-900" data-id="element-188" data-mp="2azvmxj5hz73d">
              {campaign.name}
            </h3>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink-500" data-id="element-189" data-mp="2ezctyh0kocan">
              {campaign.description}
            </p>
          </div>
          <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${campaign.status === 'Defined' ? 'bg-primary/10 text-primary' : 'bg-surface text-ink-500'}`} data-id="element-190" data-mp="2622f6lojq50z">
            {campaign.status === 'Defined' ? <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-191" data-mp="3md6jiez0ni2y" /> : <CircleDashedIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-192" data-mp="3qg32i6sgxg7f" />}
            {campaign.status}
          </span>
        </li>)}
    </ol>;
}
function MarketsPanel() {
  return <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-id="element-193" data-mp="1apumf6us99xu">
      {markets.map(market => <article key={market.id} className="rounded-2xl border border-line bg-white p-6" data-id="element-194" data-mp="1olf4k02oto47">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-surface font-display text-sm font-semibold text-ink-900" data-id="element-195" data-mp="1yhi36vybz4me">
            {market.code}
          </span>
          <h3 className="mt-4 font-display text-lg font-semibold text-ink-900" data-id="element-196" data-mp="27i7x1u416kxh">
            {market.name}
          </h3>
          <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-ink-500" data-id="element-197" data-mp="22ilklmw118pq">
            <MapPinIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-198" data-mp="d46vv2l0dlu8" />
            {market.deadline}
          </p>
        </article>)}
    </div>;
}